'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useClearRefinements, useRefinementList } from 'react-instantsearch';

import Tag from '@commons/components/Tag';
import Flex from '@commons/containers/Flex';
import { generateId } from '@commons/helpers/string';

import styles from './TopicFilter.module.scss';

/**
 * TopicFilter component.
 *
 * Renders a refinement list widget to filter the topic facet.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/refinement-list/react/
 */

const TopicFilter = () => {
  const t = useTranslations('search.filters.topic');

  const { items, refine, toggleShowMore, canToggleShowMore, isShowingMore } = useRefinementList({
    sortBy: ['name:asc'],
    operator: 'or',
    attribute: 'topics',
    limit: 20,
    showMore: true,
    showMoreLimit: 50,
  });

  const { refine: clear, canRefine: canClear } = useClearRefinements({
    includedAttributes: ['topics'],
  });

  const renderedItems = items.map(({ label, value, isRefined }) => {
    const id = generateId(`topic-${value}`);
    const classes = clsx(styles.item, isRefined && styles.active);

    return (
      <button className={classes} onClick={() => refine(value)} key={id}>
        <Tag variant="secondary" active={isRefined} level="label-md" className={styles.tag}>
          {label}
        </Tag>
      </button>
    );
  });

  const renderedClearButton = (
    <button className={styles.item} onClick={() => canClear && clear()} disabled={!canClear}>
      <Tag variant="secondary" active={!canClear} level="label-md" className={styles.tag}>
        {t('clear')}
      </Tag>
    </button>
  );

  const renderedShowMoreButton = (
    <button className={styles.item} onClick={toggleShowMore}>
      <Tag variant="secondary" level="label-md" className={styles.tag}>
        {t('showMore')}
      </Tag>
    </button>
  );

  return (
    <Flex gap="xxs" wrap="wrap">
      {renderedClearButton}
      {renderedItems}
      {canToggleShowMore && !isShowingMore && renderedShowMoreButton}
    </Flex>
  );
};

export default TopicFilter;
